// src/admin/components/DataTable.jsx
import React, { useMemo, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, TablePagination, TextField, Paper,
} from '@mui/material';

export default function DataTable({ columns, rows, searchable = false, searchKeys, initialSort, rowActions }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState(initialSort?.key || null);
  const [order, setOrder] = useState(initialSort?.dir || 'asc');

  const keys = searchKeys || columns.map((c) => c.key);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let r = rows;
    if (q) r = r.filter((row) => keys.some((k) => String(row[k] ?? '').toLowerCase().includes(q)));
    if (orderBy) {
      r = [...r].sort((a, b) => {
        const av = String(a[orderBy] ?? '').toLowerCase();
        const bv = String(b[orderBy] ?? '').toLowerCase();
        if (av < bv) return order === 'asc' ? -1 : 1;
        if (av > bv) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return r;
  }, [rows, search, orderBy, order, keys]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (key) => {
    if (orderBy === key) setOrder(order === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(key); setOrder('asc'); }
  };

  return (
    <Paper>
      {searchable && (
        <Box sx={{ p: 2 }}>
          <TextField
            size="small" fullWidth placeholder="Cerca..."
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
        </Box>
      )}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key}>
                  {c.sortable ? (
                    <TableSortLabel active={orderBy === c.key} direction={orderBy === c.key ? order : 'asc'} onClick={() => handleSort(c.key)}>
                      {c.label}
                    </TableSortLabel>
                  ) : c.label}
                </TableCell>
              ))}
              {rowActions && <TableCell align="right">Azioni</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((row) => (
              <TableRow key={row.id} hover>
                {columns.map((c) => (
                  <TableCell key={c.key}>{c.render ? c.render(row) : row[c.key]}</TableCell>
                ))}
                {rowActions && <TableCell align="right">{rowActions(row)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div" count={filtered.length} page={page}
        onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Paper>
  );
}
